/**
 * Instanced ground cover (E3.3.S5, AD-159) — the thin `three` half of
 * groundCover.ts. One blade-clump geometry per tier, one `InstancedMesh` per
 * non-empty chunk (AD-158 §2: never a per-blade `THREE.Mesh`), and a
 * `MeshStandardMaterial` whose vertex stage bends each blade in the wind and
 * whose fragment stage adds the AD-159 quality bar:
 *
 *   - per-clump colour variation — `instanceColor` (one tint per clump);
 *   - ground-colour projection   — the blade base fades into the ground colour,
 *                                   so the roots read as growing out of it;
 *   - translucency              — a backlit fringe when the sun is behind the blade;
 *   - Fresnel rim               — silhouette definition at grazing angles.
 *
 * WebGL2 + GLSL via `onBeforeCompile` — the DEFAULT-path implementation, like
 * groundMaterial.ts. WebGPURenderer never calls onBeforeCompile, so the webgpu
 * path uses the TSL sibling, groundCoverNodeMaterial.ts (q-webgpu-grass-and-flame-parity,
 * folding `q-ground-cover-tsl-sibling`): the same wind from `windCoefficients()`, the
 * same uniform values from `groundCoverUniforms()`, handed in through the renderer bag
 * exactly like the ground (AD-652). It reads each clump's origin + height and tint from
 * per-instance `aGcOrigin` / `aGcTint` attributes in place of the instance matrix and
 * `instanceColor`; only a factory that asks (`nodeInstanceData`) gets them, so the WebGL2
 * default builds and uploads nothing new.
 *
 * Presentation only: no collider, no raycast target (`raycast` is a no-op, so
 * click-to-move never lands on a blade), no shadow casting.
 *
 * Import boundary (AD-122): frontend-local; `three` is allowed in frontend/.
 */

import * as THREE from 'three';

import {
  GROUND_COVER_TIERS,
  WIND_DEFAULTS,
  windGlsl,
  type CoverChunk,
  type GroundCoverPlan,
  type GroundCoverTierBudget,
  type WindParams,
} from './groundCover.js';

/** Name of the group holding a zone's cover chunks. */
export const GROUND_COVER_NAME = 'ground-cover';

/** Blade width at the base, metres (the blade is 1 unit tall; the instance scales Y to its height). */
const BLADE_BASE_WIDTH = 0.08;
/** Radius of the disc a clump's blades are scattered over, metres. */
const CLUMP_RADIUS = 0.14;

/**
 * One clump: `bladesPerClump` tapered blades, each `segments` tall with a
 * pointed tip, scattered over a small disc and leaning outward a little.
 * `position.y` IS the normalized blade height t (0 base … 1 tip) — the shader
 * reads it as such, so the geometry must stay unit-tall.
 */
export function createClumpGeometry(budget: GroundCoverTierBudget): THREE.BufferGeometry {
  const positions: number[] = [];
  const normals: number[] = [];
  const indices: number[] = [];
  const blades = budget.bladesPerClump;
  const segments = budget.segments;
  for (let b = 0; b < blades; b++) {
    // Deterministic golden-angle spread: the same clump on every run.
    const angle = b * 2.39996323;
    const r = CLUMP_RADIUS * Math.sqrt((b + 0.5) / blades);
    const ox = Math.cos(angle) * r;
    const oz = Math.sin(angle) * r;
    const yaw = angle * 1.7 + 0.6;
    const cx = Math.cos(yaw);
    const cz = Math.sin(yaw);
    const leanX = ox * 0.9;
    const leanZ = oz * 0.9;
    const start = positions.length / 3;
    for (let s = 0; s < segments; s++) {
      const t = s / segments;
      const half = (BLADE_BASE_WIDTH / 2) * (1 - Math.pow(t, 1.4));
      const lean = t * t;
      for (const side of [-1, 1]) {
        positions.push(
          ox + leanX * lean + cx * half * side,
          t,
          oz + leanZ * lean + cz * half * side,
        );
        normals.push(-cz, 0.35, cx);
      }
    }
    positions.push(ox + leanX, 1, oz + leanZ);
    normals.push(-cz, 0.35, cx);
    for (let s = 0; s < segments - 1; s++) {
      const a = start + s * 2;
      indices.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    const last = start + (segments - 1) * 2;
    indices.push(last, last + 1, start + segments * 2);
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  geometry.setIndex(indices);
  geometry.computeBoundingSphere();
  return geometry;
}

/** The shared per-frame uniforms one cover material owns. */
export interface GroundCoverUniforms {
  uGcTime: { value: number };
  uGcWindDir: { value: THREE.Vector2 };
  uGcBase: { value: THREE.Color };
  uGcTip: { value: THREE.Color };
  uGcGround: { value: THREE.Color };
  /** World-space direction TOWARD the sun (normalized). */
  uGcSunDir: { value: THREE.Vector3 };
  uGcTranslucency: { value: number };
  uGcRim: { value: number };
}

export interface GroundCoverMaterialOptions {
  /** The ground's dominant colour (sRGB hex) the blade bases fade into. */
  groundColor: string;
  /** World position of the sun light (direction toward it is derived). */
  sun: { x: number; y: number; z: number };
  wind?: WindParams;
}

/** What a cover material factory hands back. */
export interface GroundCoverMaterialResult {
  material: THREE.Material;
  uniforms: GroundCoverUniforms;
  /**
   * The material reads its per-clump data from node-friendly instance attributes — the TSL
   * sibling does: `aGcOrigin` (x, y, z, height) in place of the instance matrix (which a
   * node graph cannot read before three applies it), and `aGcTint` (rgb) in place of
   * `instanceColor` (which three multiplies over the WHOLE colour node, ground fade
   * included; the GLSL tints only the blade). The GLSL sibling leaves this unset: nothing
   * extra is built or uploaded on the default path.
   */
  nodeInstanceData?: boolean;
}

export type GroundCoverMaterialFactory = (
  plan: GroundCoverPlan,
  options: GroundCoverMaterialOptions,
) => GroundCoverMaterialResult;

/** Translucency (backlit fringe) and Fresnel rim strengths — the AD-159 bar, kept subtle. */
export const GROUND_COVER_TRANSLUCENCY = 0.45;
export const GROUND_COVER_RIM = 0.22;
/** The blade base fades into the ground colour as t runs 0 → this. */
export const GROUND_COVER_ROOT_FADE = 0.3;

/**
 * The uniform VALUES one cover material starts from — shared by both siblings, so the
 * GLSL and TSL meadows get the same colours, wind direction and sun.
 */
export function groundCoverUniforms(
  plan: GroundCoverPlan,
  options: GroundCoverMaterialOptions,
): GroundCoverUniforms {
  const wind = options.wind ?? WIND_DEFAULTS;
  return {
    uGcTime: { value: 0 },
    uGcWindDir: { value: new THREE.Vector2(wind.dirX, wind.dirZ).normalize() },
    uGcBase: { value: new THREE.Color(plan.style.base) },
    uGcTip: { value: new THREE.Color(plan.style.tip) },
    uGcGround: { value: new THREE.Color(options.groundColor) },
    uGcSunDir: {
      value: new THREE.Vector3(options.sun.x, options.sun.y, options.sun.z).normalize(),
    },
    uGcTranslucency: { value: GROUND_COVER_TRANSLUCENCY },
    uGcRim: { value: GROUND_COVER_RIM },
  };
}

/** Replace one include anchor, or throw — a three upgrade that renames a chunk must fail loudly. */
function replaceAnchor(source: string, anchor: string, replacement: string): string {
  if (!source.includes(anchor)) throw new Error(`ground cover: shader anchor ${anchor} not found`);
  return source.replace(anchor, replacement);
}

/**
 * Patch three's standard shader pair with the cover wind + blade shading.
 * Exported so tests run it against the REAL shipped `ShaderLib.standard` source.
 */
export function patchGroundCoverShader(
  vertexShader: string,
  fragmentShader: string,
  glsl: string,
): { vertexShader: string; fragmentShader: string } {
  let vs = replaceAnchor(
    vertexShader,
    '#include <common>',
    `#include <common>
uniform float uGcTime;
uniform vec2 uGcWindDir;
attribute float aGcPhase;
varying float vGcT;
${glsl}`,
  );
  vs = replaceAnchor(
    vs,
    '#include <begin_vertex>',
    `vec3 transformed = vec3(position);
float gcT = clamp(position.y, 0.0, 1.0);
vGcT = gcT;
#ifdef USE_INSTANCING
mat4 gcInstance = instanceMatrix;
#else
mat4 gcInstance = mat4(1.0);
#endif
vec2 gcOrigin = (modelMatrix * gcInstance * vec4(0.0, 0.0, 0.0, 1.0)).xz;
float gcHeight = length(gcInstance[1].xyz);
float gcAlong = dot(gcOrigin, uGcWindDir);
float gcPhi = gcBendAngle(gcAlong, uGcTime, aGcPhase, gcJitter(gcOrigin * 0.03));
vec2 gcArc = gcArcBend(gcT, gcHeight, gcPhi);
float gcSide = gcTipFlutter(gcT, uGcTime, aGcPhase, gcAlong) * gcHeight;
vec3 gcWorld = vec3(uGcWindDir.x * gcArc.x - uGcWindDir.y * gcSide, gcArc.y, uGcWindDir.y * gcArc.x + uGcWindDir.x * gcSide);
transformed += inverse(mat3(gcInstance)) * gcWorld;`,
  );
  let fs = replaceAnchor(
    fragmentShader,
    '#include <common>',
    `#include <common>
uniform vec3 uGcBase;
uniform vec3 uGcTip;
uniform vec3 uGcGround;
uniform vec3 uGcSunDir;
uniform float uGcTranslucency;
uniform float uGcRim;
varying float vGcT;`,
  );
  fs = replaceAnchor(
    fs,
    '#include <color_fragment>',
    `#include <color_fragment>
vec3 gcBlade = mix(uGcBase, uGcTip, vGcT) * diffuseColor.rgb;
diffuseColor.rgb = mix(uGcGround, gcBlade, smoothstep(0.0, ${String(GROUND_COVER_ROOT_FADE)}, vGcT));`,
  );
  fs = replaceAnchor(
    fs,
    '#include <emissivemap_fragment>',
    `#include <emissivemap_fragment>
vec3 gcView = normalize(vViewPosition);
vec3 gcSunView = normalize((viewMatrix * vec4(uGcSunDir, 0.0)).xyz);
float gcRimTerm = pow(1.0 - abs(dot(normal, gcView)), 3.0);
float gcBack = pow(max(dot(-gcView, gcSunView), 0.0), 4.0) * vGcT;
totalEmissiveRadiance += diffuseColor.rgb * (uGcRim * gcRimTerm + uGcTranslucency * gcBack);`,
  );
  return { vertexShader: vs, fragmentShader: fs };
}

/** The WebGL2 GLSL cover material (the default path). */ export const createGroundCoverMaterial: GroundCoverMaterialFactory =
  (plan, options) => {
    const wind = options.wind ?? WIND_DEFAULTS;
    const uniforms = groundCoverUniforms(plan, options);
    const material = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      roughness: 0.85,
      metalness: 0,
      side: THREE.DoubleSide,
    });
    material.name = `ground-cover:${plan.style.id}`;
    const glsl = windGlsl(wind);
    material.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, uniforms);
      const patched = patchGroundCoverShader(shader.vertexShader, shader.fragmentShader, glsl);
      shader.vertexShader = patched.vertexShader;
      shader.fragmentShader = patched.fragmentShader;
    };
    material.customProgramCacheKey = () => `ground-cover-v1:${JSON.stringify(wind)}`;
    return { material, uniforms };
  };

/** Colour-variation multiplier for one clump (a dry/lush shift, not a hue swap). */
function tintMultiplier(
  tint: number,
  phase: number,
  variation: number,
  out: THREE.Color,
): THREE.Color {
  const d = (tint - 0.5) * variation;
  const v = 1 + (phase - 0.5) * variation * 0.5;
  return out.setRGB((1 + d * 0.9) * v, (1 + d * 0.35) * v, (1 - d * 0.3) * v);
}

/** One chunk as ONE InstancedMesh (one draw call). */
export function createCoverChunkMesh(
  chunk: CoverChunk,
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  variation: number,
  nodeInstanceData = false,
): THREE.InstancedMesh {
  const count = chunk.clumps.length;
  const mesh = new THREE.InstancedMesh(geometry, material, count);
  mesh.name = 'ground-cover-chunk';
  const phases = new Float32Array(count);
  // The TSL sibling's per-clump data (GroundCoverMaterialResult.nodeInstanceData).
  const origins = nodeInstanceData ? new Float32Array(count * 4) : null;
  const tints = nodeInstanceData ? new Float32Array(count * 3) : null;
  const matrix = new THREE.Matrix4();
  const quat = new THREE.Quaternion();
  const up = new THREE.Vector3(0, 1, 0);
  const pos = new THREE.Vector3();
  const scale = new THREE.Vector3();
  const colour = new THREE.Color();
  chunk.clumps.forEach((clump, i) => {
    quat.setFromAxisAngle(up, clump.rotationY);
    matrix.compose(pos.set(clump.x, clump.y, clump.z), quat, scale.set(1, clump.height, 1));
    mesh.setMatrixAt(i, matrix);
    tintMultiplier(clump.tint, clump.phase, variation, colour);
    if (tints) colour.toArray(tints, i * 3);
    else mesh.setColorAt(i, colour);
    phases[i] = clump.phase;
    origins?.set([clump.x, clump.y, clump.z, clump.height], i * 4);
  });
  geometry.setAttribute('aGcPhase', new THREE.InstancedBufferAttribute(phases, 1));
  if (origins) geometry.setAttribute('aGcOrigin', new THREE.InstancedBufferAttribute(origins, 4));
  if (tints) geometry.setAttribute('aGcTint', new THREE.InstancedBufferAttribute(tints, 3));
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  mesh.computeBoundingSphere();
  // Wind bends a blade up to its own height sideways; pad the cull sphere by the tallest.
  if (mesh.boundingSphere) mesh.boundingSphere.radius += 1;
  mesh.castShadow = false;
  mesh.receiveShadow = true;
  mesh.frustumCulled = true;
  mesh.raycast = () => {};
  return mesh;
}

/** The realized cover: its group, its chunks (for the distance cull) and its clock. */
export interface GroundCoverHandle {
  group: THREE.Group;
  chunks: { chunk: CoverChunk; mesh: THREE.InstancedMesh }[];
  uniforms: GroundCoverUniforms;
}

/**
 * Realize a cover plan. Each chunk gets its OWN geometry clone because the
 * per-instance phase attribute lives on the geometry; the clump vertex data is
 * tiny (≤ 45 vertices), so the clones cost nothing that matters.
 */
export function buildGroundCover(
  plan: GroundCoverPlan,
  options: GroundCoverMaterialOptions,
  factory: GroundCoverMaterialFactory = createGroundCoverMaterial,
): GroundCoverHandle {
  const { material, uniforms, nodeInstanceData = false } = factory(plan, options);
  const template = createClumpGeometry(GROUND_COVER_TIERS[plan.tier]);
  const group = new THREE.Group();
  group.name = GROUND_COVER_NAME;
  const chunks = plan.chunks.map((chunk) => {
    const mesh = createCoverChunkMesh(
      chunk,
      template.clone(),
      material,
      plan.style.variation,
      nodeInstanceData,
    );
    group.add(mesh);
    return { chunk, mesh };
  });
  template.dispose();
  return { group, chunks, uniforms };
}
